import { createSelector } from '@reduxjs/toolkit';

import { ISG_FOLDER_EXTERNAL_ID } from '@iconik-io/core/src/constants/isg';
import type { FilesStorageSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/files_storages';
import type {
  FilesFileSchemaWithId,
  FilesFormatSchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { selectUserRoles } from '@iconik-io/shared-lib/src/redux/auth/selectors';
import { findOriginalCloudFormat } from '@iconik-io/shared-lib/src/redux/files/thunks';
import { CLOUD_STORAGE_METHODS } from '@iconik-io/shared-lib/src/utils/constants/storages';

import selectMergedPermissions from '../../components/permissions_manager/selectors';
import {
  ASSET_PAGE_ROUTE,
  ASSET_VERSION_PAGE_ROUTE,
  COLLECTION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
  UPLOAD_PAGE_ROUTE,
} from '../../constants/routes';
import { selectFileSetsByAssetId } from '../../ducks/file_sets/selectors';
import {
  selectAssetFiles,
  selectAssetFormats,
  selectAssetOriginalFormats,
} from '../../ducks/pages/asset/selectors';
import { selectIsLatestVersion } from '../../ducks/version/selectors';
import type { AppState } from '../../store/types';
import { isCollectionMappedToStorage } from '../../utils/collections';
import { selectUserRoles as selectUserRolesDeprecated } from '../../utils/get_user_roles';
import { getAsset, getCollection } from './helpers';
import { hasUploadRoles } from './uploader_roles';

const isAssetOrAssetVersionRoute = (route: string) =>
  route === ASSET_PAGE_ROUTE || route === ASSET_VERSION_PAGE_ROUTE;

const getFormats = (
  state: AppState,
  entityId: string | undefined,
  versionId: string | undefined,
) => selectAssetFormats(state, entityId, versionId);

const getFiles = (
  state: AppState,
  entityId: string | undefined,
  versionId: string | undefined,
) => selectAssetFiles(state, entityId, versionId);

const getStorages = (state: AppState) => state.entities.storages;

const getDefaultStorage = (state: AppState) =>
  state.storageMatchingReducer.filesStorageMatched?.id;

// Route and entity id are supplied together by the caller so they always
// describe the same match; see PanelsContainer.
const getParamRoute = (
  _state: AppState,
  _entityId: string | undefined,
  _versionId: string | undefined,
  route: string,
) => route;

const getCurrentCollection = (state: AppState, entityId: string | undefined) =>
  getCollection(state, entityId);

const getShareSettings = (state: AppState) => state.share.share;

const getSelectedStorage = (state: AppState) =>
  state.uploaderReducer.selectedStorage;

const getAssetPermissions = (state: AppState, id: string | undefined) =>
  selectMergedPermissions(state, OBJECT_TYPE_ASSETS, id);

const getCollectionPermissions = (state: AppState, id: string | undefined) =>
  selectMergedPermissions(state, OBJECT_TYPE_COLLECTIONS, id);

export const selectEntityType = createSelector([getParamRoute], (route) => {
  let entityType;
  if (isAssetOrAssetVersionRoute(route)) {
    entityType = OBJECT_TYPE_ASSETS;
  }
  if (route === COLLECTION_PAGE_ROUTE) {
    entityType = OBJECT_TYPE_COLLECTIONS;
  }
  if (route === SHARE_COLLECTION_PAGE_ROUTE) {
    entityType = OBJECT_TYPE_COLLECTIONS;
  }
  return entityType;
});

const getExpanded = (state: AppState) => state.uploaderReducer.expanded;

export const selectHasRolesToUpload = createSelector(
  [
    selectUserRolesDeprecated,
    selectIsLatestVersion,
    selectEntityType,
    getCurrentCollection,
  ],
  (roles, isLastVersion, objectType, collection) => {
    const isRootMappedCollection =
      isCollectionMappedToStorage(collection) && collection?.is_root;

    if (isRootMappedCollection && !roles.webCanTopLevelUpload) {
      return false;
    }

    const hasObjectTypeRoles = Boolean(
      (isLastVersion && objectType === OBJECT_TYPE_ASSETS) ||
        objectType === OBJECT_TYPE_COLLECTIONS ||
        (objectType === undefined && roles.webCanTopLevelUpload),
    );
    const hasRoles = hasUploadRoles(roles) && hasObjectTypeRoles;
    return hasRoles;
  },
);

export const selectIsCurrentCollectionISG = createSelector(
  [getCollection, getStorages],
  (collection, storages) =>
    collection?.storage_id &&
    storages[collection.storage_id]?.method === 'FILE',
);

const getIsEditSortMode = (state: AppState) =>
  state.pages.collection.editSortMode;

export const selectCanUploadByPermissions = createSelector(
  [
    getAssetPermissions,
    getCollectionPermissions,
    getCollection,
    getAsset,
    getIsEditSortMode,
    getShareSettings,
  ],
  (
    assetPermissions,
    collectionPermissions,
    collection,
    asset,
    isEditSortMode,
    sharePermissions,
  ) => {
    if (window.PLATFORM !== 'WEB') {
      return false;
    }
    if (isEditSortMode) {
      return false;
    }

    // if no asset or collection permissions are not required.
    // it means uploading to the global context. set hasPermissions = true.
    let hasPermissions = !asset && !collection;

    if (collection && collection.external_id === ISG_FOLDER_EXTERNAL_ID) {
      return false;
    }
    if (collection && collectionPermissions?.write) {
      hasPermissions = true;
    }
    if (collection && sharePermissions.allow_upload) {
      hasPermissions = true;
    }
    if (asset && assetPermissions?.write) {
      hasPermissions = true;
    }

    return hasPermissions;
  },
);

export const selectCanUpload = createSelector(
  [selectHasRolesToUpload, selectCanUploadByPermissions],
  (hasRoles, hasPermissions) => hasRoles && hasPermissions,
);

export const selectCanDropFiles = createSelector(
  [
    selectHasRolesToUpload,
    getIsEditSortMode,
    getShareSettings,
    getCollection,
    getStorages,
    getAsset,
  ],
  (hasRoles, isEditSortMode, shareSettings, collection, storages, asset) => {
    const storage = collection?.storage_id
      ? storages[collection?.storage_id]
      : null;

    const isArchivedCollection = storage?.purpose === 'ARCHIVE';

    const isSubclip = asset?.type === 'SUBCLIP';

    if (window.PLATFORM !== 'WEB') {
      return false;
    }
    if (isEditSortMode) {
      return false;
    }

    if (isArchivedCollection) {
      return false;
    }

    if (isSubclip) {
      return false;
    }

    if (shareSettings.id && !shareSettings.allow_upload) {
      return false;
    }

    return hasRoles;
  },
);

const findCloudFileStorageId = (
  formats: FilesFormatSchemaWithId[],
  files: FilesFileSchemaWithId[],
  storages: FilesStorageSchemaPatched[],
): string | undefined => {
  const cloudFormat = findOriginalCloudFormat(formats)?.id;
  if (cloudFormat) {
    const cloudFileStorageId = files.find((file) => {
      const storage = storages.some((item) => item.id === file.storage_id);
      return file.url && file.format_id === cloudFormat && storage;
    })?.storage_id;
    return cloudFileStorageId;
  }
  return undefined;
};

export const selectCollectionUploadStorageId = createSelector(
  [getCollection, getStorages],
  (collection, storages) => {
    if (collection?.storage_id) {
      const storage = storages[collection.storage_id];
      if (storage && storage.method !== 'FILE') {
        return storage.id;
      }
    }
    return undefined;
  },
);

export const selectWritableUploadStorages = createSelector(
  selectUserRoles,
  getStorages,
  (state: AppState) => state.acls.storages.entityCurrentUserAcls,
  (userRoles, storages, storageAcls) => {
    const writableCloudStorages = Object.values(storages).filter(
      (storage) =>
        storage.purpose === 'FILES' &&
        CLOUD_STORAGE_METHODS.includes(storage.method) &&
        storage.settings.write,
    );
    return writableCloudStorages.filter((storage) => {
      // If we have the upload legacy role enabled then we don't check the storage ACLs
      // See https://linear.app/iconik/issue/AUT-516/add-new-web-can-upload-legacy-role
      if (userRoles.web_can_upload_legacy) {
        return true;
      }
      return (
        Boolean(storageAcls[storage.id]?.write) &&
        Boolean(storageAcls[storage.id]?.read)
      );
    });
  },
);

export const selectAssetUploadStorageId = createSelector(
  [getFormats, getFiles, getParamRoute, selectWritableUploadStorages],
  (formats, files, route, writableUploadStorages) => {
    if (isAssetOrAssetVersionRoute(route)) {
      const storageId = findCloudFileStorageId(
        formats,
        files,
        writableUploadStorages,
      );
      return storageId;
    }
    return undefined;
  },
);

export const selectUploadStorage = createSelector(
  [
    getDefaultStorage,
    getSelectedStorage,
    selectAssetUploadStorageId,
    selectCollectionUploadStorageId,
  ],
  (defaultStorage, selectedStorage, assetStorageId, collectionStorageId) => {
    if (collectionStorageId) {
      return collectionStorageId;
    }
    if (selectedStorage) {
      return selectedStorage;
    }
    if (assetStorageId) {
      return assetStorageId;
    }
    return defaultStorage;
  },
);

export const selectDefaultStorage = createSelector(
  [
    getFormats,
    getFiles,
    getCollection,
    getDefaultStorage,
    getParamRoute,
    selectWritableUploadStorages,
  ],
  (
    formats,
    files,
    collection,
    defaultStorage,
    route,
    writableUploadStorages,
  ) => {
    if (route === COLLECTION_PAGE_ROUTE && collection?.storage_id) {
      return collection?.storage_id;
    }

    if (isAssetOrAssetVersionRoute(route)) {
      return (
        findCloudFileStorageId(formats, files, writableUploadStorages) ??
        defaultStorage
      );
    }
    return defaultStorage;
  },
);

export const selectStorageOptionDisabled = createSelector(
  [getCollection, selectIsCurrentCollectionISG],
  (collection, isCurrentCollectionISG): boolean => {
    if (isCurrentCollectionISG) {
      return false;
    }
    return Boolean(collection?.storage_id);
  },
);

export const selectIsUploaderFullScreen = createSelector(
  [getParamRoute],
  (route): boolean => Boolean(route === UPLOAD_PAGE_ROUTE),
);

export const selectIsUploaderExpanded = getExpanded;

export const selectIfCanChooseUploadStorage = createSelector(
  [getStorages],
  (storages): boolean =>
    Object.values(storages).filter(
      (storage) =>
        storage.purpose === 'FILES' &&
        storage.settings.write &&
        CLOUD_STORAGE_METHODS.includes(storage.method),
    ).length > 1,
);

export const findAssetPathInStorage = createSelector(
  [
    selectAssetOriginalFormats,
    selectFileSetsByAssetId,
    (
      state: AppState,
      _assetId: string | undefined,
      _versionId: string | undefined,
      storageId?: string,
    ) =>
      storageId
        ? state.entities.storages[storageId]
        : state.storageMatchingReducer.filesStorageMatched,
  ],
  (formats, fileSets, storage) => {
    if (!storage) {
      return '';
    }

    const formatMatch = formats.find((format) =>
      format.storage_methods?.includes(storage.method),
    );

    if (!formatMatch) {
      return '';
    }

    const fileSetMatch = fileSets.find(
      (fileSet) =>
        fileSet.format_id === formatMatch.id &&
        fileSet.storage_id === storage.id,
    );

    return fileSetMatch?.base_dir ?? '';
  },
);
